import { ChangeDetectionStrategy, Component, computed, ElementRef, OnInit, OnDestroy, signal, viewChild, afterNextRender } from '@angular/core';

interface Snowflake {
    x: number;
    y: number;
    radius: number;
    speed: number;
    drift: number;
    opacity: number;
    windOffset: number;
    wobble: number;
    wobbleSpeed: number;
    depth: number; // For parallax effect
}

@Component({
    selector: 'sphere-holiday-snow-effect',
    standalone: true,
    template: `
        <canvas #snowCanvas class="snow-canvas"></canvas>
        @if (isHolidayPeriod()) {
            <div class="decorations">
                <div class="decoration top-left">
                    <img src="/assets/images/christmas-1.svg" alt="Christmas decoration snow" />
                </div>
                <div class="decoration top-left-2">
                    <div class="scale-0-8">
                        <img src="/assets/images/christmas-1.svg" alt="Christmas decoration snow" />
                    </div>
                </div>
                <div class="decoration top-right"></div>
                <div class="decoration bottom-left"></div>
                <div class="decoration bottom-right"></div>
                <div class="decoration center-accent">
                    <div class="scale-25">
                        <img src="/assets/images/christmas-2.svg" alt="Christmas decoration accent" />
                    </div>
                </div>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 50;
                overflow: hidden;
            }

            .snow-canvas {
                width: 100%;
                height: 100%;
                display: block;
            }

            .decorations {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
            }

            .decoration {
                position: absolute;

                svg {
                    filter: drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3));
                }
            }

            @keyframes up-down {
                0% {
                    transform: translateY(-5px);
                }
                50% {
                    transform: translateY(0);
                }
                100% {
                    transform: translateY(-5px);
                }
            }

            @keyframes shake {
                0% {
                    transform: rotateZ(0deg);
                }
                50% {
                    transform: rotateZ(1deg);
                }
                75% {
                    transform: rotateZ(-1deg);
                }
                100% {
                    transform: rotateZ(0deg);
                }
            }

            .top-left {
                opacity: 0.75;
                top: 0;
                left: 30px;
                animation: up-down 2s ease-in-out infinite;
            }

            .scale-0-8 {
                transform-origin: top left;
                transform: scale(0.8);
            }
            .scale-25 {
                transform-origin: bottom right;
                transform: scale(0.25);
            }
            .top-left-2 {
                opacity: 0.6;
                top: 0;
                left: 100px;
                animation: up-down 2.3s ease-in-out infinite;
            }

            .center-accent {
                bottom: 10px;
                right: 10px;
                filter: brightness(120%) drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.2));
                animation: shake 10s ease-in-out infinite;
            }

            .top-right {
                top: 40px;
                right: 40px;
                animation-delay: 0.8s;
            }

            .bottom-left {
                bottom: 40px;
                left: 40px;
                animation-delay: 1.1s;
            }

            .bottom-right {
                bottom: 30px;
                right: 30px;
                animation-delay: 1.4s;
            }

            @keyframes fadeInDecoration {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 0.7;
                }
            }

            @keyframes rotateSnowflake {
                from {
                    transform: rotate(0deg);
                }
                to {
                    transform: rotate(360deg);
                }
            }

            @keyframes gentleSway {
                0%,
                100% {
                    transform: rotate(0deg) translateY(0);
                }
                50% {
                    transform: rotate(3deg) translateY(-2px);
                }
            }

            @media (max-width: 768px) {
                .decoration {
                    display: none;
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HolidaySnowEffectComponent implements OnInit, OnDestroy {
    private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('snowCanvas');

    private readonly snowflakes = signal<Snowflake[]>([]);
    private animationFrameId: number | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private wind = 0;
    private windTarget = 0;
    private gustTimer = 0;

    public readonly isHolidayPeriod = computed(() => {
        const now = new Date();
        const month = now.getMonth(); // 0-indexed (0 = January, 11 = December)
        const day = now.getDate();

        // December 20 to December 31
        if (month === 11) {
            return true;
        }

        // January 1
        if (month === 0 && day === 1) {
            return true;
        }

        return false;
    });

    constructor() {
        afterNextRender(() => {
            const canvas = this.canvasRef()?.nativeElement;
            if (canvas && this.isHolidayPeriod()) {
                this.initializeSnowEffect(canvas);
            }
        });
    }

    ngOnInit(): void {
        // Initialization handled in afterNextRender
    }

    private initializeSnowEffect(canvas: HTMLCanvasElement): void {
        this.ctx = canvas.getContext('2d', { alpha: true });
        if (!this.ctx) return;

        this.resizeCanvas(canvas);
        this.createSnowflakes(canvas);
        this.animate();

        // Handle window resize
        window.addEventListener('resize', () => this.resizeCanvas(canvas));
    }

    private resizeCanvas(canvas: HTMLCanvasElement): void {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        // Recreate snowflakes on resize to maintain proper density
        if (this.ctx) {
            this.createSnowflakes(canvas);
        }
    }

    private createSnowflakes(canvas: HTMLCanvasElement): void {
        const flakes: Snowflake[] = [];
        // Adjust density: fewer flakes for better performance and more elegant look
        const numFlakes = Math.floor((canvas.width * canvas.height) / 12000);

        for (let i = 0; i < numFlakes; i++) {
            flakes.push(this.createSnowflake(canvas, Math.random() * canvas.height));
        }

        this.snowflakes.set(flakes);
    }

    private createSnowflake(canvas: HTMLCanvasElement, yPosition?: number): Snowflake {
        const depth = Math.random(); // 0 to 1, for parallax

        return {
            x: Math.random() * canvas.width,
            y: yPosition ?? -10,
            radius: Math.random() * 2 + 0.5, // 0.5 to 2.5px
            speed: (Math.random() * 0.5 + 0.3) * (0.5 + depth * 0.5), // Depth-based speed
            drift: (Math.random() * 0.5 - 0.25) * (0.5 + depth * 0.5),
            opacity: (Math.random() * 0.4 + 0.3) * (0.6 + depth * 0.4), // 0.3 to 0.7
            windOffset: Math.random() * 100,
            wobble: Math.random() * Math.PI * 2,
            wobbleSpeed: Math.random() * 0.015 + 0.008,
            depth: depth
        };
    }

    private updateWind(): void {
        // Simulate realistic wind gusts
        this.gustTimer++;

        if (this.gustTimer > 400) {
            // Change wind every ~6.5 seconds at 60fps
            this.windTarget = (Math.random() - 0.5) * 1.5; // Wind between -0.75 and 0.75
            this.gustTimer = 0;
        }

        // Smoothly interpolate to target wind
        this.wind += (this.windTarget - this.wind) * 0.008;
    }

    private animate = (): void => {
        if (!this.ctx || !this.canvasRef()) return;

        const canvas = this.canvasRef().nativeElement;
        const ctx = this.ctx;

        // Clear canvas with slight fade for motion blur effect
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Update wind
        this.updateWind();

        // Update and draw snowflakes
        const flakes = this.snowflakes();
        const updatedFlakes = flakes.map(flake => {
            // Update wobble for realistic swaying
            flake.wobble += flake.wobbleSpeed;

            // Apply wind effect with wobble, scaled by depth
            const windEffect = this.wind * (1 + Math.sin(flake.wobble) * 0.4) * flake.depth;
            flake.x += flake.drift + windEffect;
            flake.y += flake.speed;

            // Add subtle horizontal wobble
            flake.x += Math.sin(flake.wobble) * 0.4 * flake.depth;

            // Reset snowflake if it goes off screen
            if (flake.y > canvas.height + 10) {
                return this.createSnowflake(canvas);
            }

            if (flake.x > canvas.width + 20) {
                flake.x = -20;
            } else if (flake.x < -20) {
                flake.x = canvas.width + 20;
            }

            // Draw snowflake with ultra-realistic rendering
            ctx.save();
            ctx.globalAlpha = flake.opacity;

            // Create radial gradient for soft, glowing effect
            const gradient = ctx.createRadialGradient(flake.x, flake.y, 0, flake.x, flake.y, flake.radius * 2);
            gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
            gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.8)');
            gradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.3)');
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(flake.x, flake.y, flake.radius * 2, 0, Math.PI * 2);
            ctx.fill();

            // Add a bright crystalline core
            ctx.fillStyle = `rgba(255, 255, 255, ${0.9 * flake.opacity})`;
            ctx.beginPath();
            ctx.arc(flake.x, flake.y, flake.radius * 0.4, 0, Math.PI * 2);
            ctx.fill();

            // Add subtle sparkle effect for larger flakes
            if (flake.radius > 1.5 && Math.random() > 0.97) {
                ctx.fillStyle = `rgba(255, 255, 255, ${0.6 * flake.opacity})`;
                ctx.beginPath();
                ctx.arc(flake.x + 0.5, flake.y - 0.5, flake.radius * 0.2, 0, Math.PI * 2);
                ctx.fill();
            }

            ctx.restore();

            return flake;
        });

        this.snowflakes.set(updatedFlakes);

        // Continue animation
        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    private cleanup(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        if (this.ctx && this.canvasRef()) {
            const canvas = this.canvasRef().nativeElement;
            this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    ngOnDestroy(): void {
        this.cleanup();
        const canvas = this.canvasRef()?.nativeElement;
        if (canvas) {
            window.removeEventListener('resize', () => this.resizeCanvas(canvas));
        }
    }
}
